import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';
const setImageHeight = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = new URL(imgSrc);
  const height = url.searchParams.get('cw_image_height');
  if (!height) return;
  inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageHeight(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // Custom rule for image resize in markdown
  // If the image url has a query param cw_image_height, then add a style attribute to the image
  md.core.ruler.after('inline', 'add-image-height', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

const md = MarkdownIt({
  html: false,
  xhtmlOut: true,
  breaks: true,
  langPrefix: 'language-',
  linkify: true,
  typographer: true,
  quotes: '\u201c\u201d\u2018\u2019',
  maxNesting: 20,
})
  .use(mentionPlugin)
  .use(imgResizeManager)
  .use(mila, {
    attrs: {
      class: 'link',
      rel: 'noreferrer noopener nofollow',
      target: '_blank',
    },
  });

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';
const IMAGE_URL_REGEX =
  /(^|\s)(https?:\/\/[^\s<>()]+?\.(?:png|jpe?g|gif|webp|svg)(?:\?[^\s<>()]*)?(?:#[^\s<>()]*)?)/gi;

const replaceImageUrls = message => {
  if (!message) return message;
  return message.replace(IMAGE_URL_REGEX, (match, prefix, url, offset) => {
    const urlIndex = offset + prefix.length;
    const beforeUrl = message.slice(Math.max(0, urlIndex - 4), urlIndex);
    const beforeLinkUrl = message.slice(Math.max(0, urlIndex - 2), urlIndex);
    if (beforeUrl === '![](' || beforeLinkUrl === '](') {
      return match;
    }
    return `${prefix}![](${url})`;
  });
};

// Links para arquivos (ex.: cabeçalho DOCUMENT de templates WhatsApp) viram bloco com ícone por extensão.
const FILE_ATTACHMENT_HREF =
  /\.(pdf|docx?|xlsx?|pptx?|zip|rar|7z|csv|txt|odt|rtf)(\?|#|$)/i;
const VIDEO_ATTACHMENT_HREF = /\.(mp4|mov|webm|ogg)(\?|#|$)/i;
const VIDEO_LINK_TEXT = /^(video|vídeo)$/i;

function isTemplateVideoParagraph(element) {
  if (!element || element.tagName !== 'P') return false;

  const textContent = element.textContent.replace(/\s+/g, ' ').trim();
  return /^🎥\s*(video|vídeo)$/i.test(textContent);
}

function buildInlineVideo(doc, href) {
  const wrap = doc.createElement('span');
  wrap.className = 'cw-inline-video-attachment';

  const video = doc.createElement('video');
  video.className = 'cw-inline-video-attachment__player skip-context-menu';
  video.setAttribute('src', href);
  video.setAttribute('controls', 'controls');
  video.setAttribute('preload', 'metadata');
  video.setAttribute('playsinline', 'playsinline');

  wrap.appendChild(video);
  return wrap;
}

function postProcessMediaAttachmentLinks(html) {
  if (!html || typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html;
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(
      `<div class="cw-file-links-root">${html}</div>`,
      'text/html'
    );
    const root = doc.body.querySelector('.cw-file-links-root');
    if (!root) return html;

    root.querySelectorAll('a[href]').forEach(a => {
      const href = a.getAttribute('href') || '';
      const linkText = (a.textContent || '').trim();

      if (
        VIDEO_ATTACHMENT_HREF.test(href) ||
        (VIDEO_LINK_TEXT.test(linkText) && isTemplateVideoParagraph(a.parentElement))
      ) {
        const video = buildInlineVideo(doc, href);
        if (isTemplateVideoParagraph(a.parentElement)) {
          a.parentElement.replaceWith(video);
        } else {
          a.replaceWith(video);
        }
        return;
      }

      if (!FILE_ATTACHMENT_HREF.test(href)) return;
      if (a.closest('.cw-inline-file-attachment')) return;

      const extMatch = href.match(/\.([^.?#]+)(?:\?|#|$)/i);
      const ext = (extMatch && extMatch[1] ? extMatch[1] : 'file').toLowerCase();

      const wrap = doc.createElement('span');
      wrap.className = 'cw-inline-file-attachment';
      wrap.setAttribute('data-ext', ext);

      const icon = doc.createElement('span');
      icon.className = 'cw-inline-file-attachment__icon';
      icon.setAttribute('aria-hidden', 'true');

      a.classList.add('cw-inline-file-attachment__link');

      const parent = a.parentNode;
      parent.insertBefore(wrap, a);
      wrap.appendChild(icon);
      wrap.appendChild(a);
    });

    return root.innerHTML;
  } catch (_e) {
    return html;
  }
}

class MessageFormatter {
  constructor(message, isATweet = false, isAPrivateNote = false, options = {}) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.showImages = options?.showImages === true;
  }

  formatMessage() {
    let updatedMessage = this.message;
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    if (this.showImages) {
      updatedMessage = replaceImageUrls(updatedMessage);
    }
    let html = md.render(updatedMessage);
    html = postProcessMediaAttachmentLinks(html);
    return html;
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
